import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Zap, ChevronDown, Search, X, Check, AlertCircle } from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';
import AlteredStatePicker from '../components/AlteredStatePicker';

const TIER_COLORS = { free: '#A0A0A0', economy: '#22C55E', standard: '#F59E0B', premium: '#EF4444' };
const TIER_ORDER = { economy: 0, standard: 1, premium: 2 };

export default function Gauntlet() {
  const { templateId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [playbook, setPlaybook] = useState(null);
  const [scenario, setScenario] = useState('');
  const [voices, setVoices] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState('');
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState('');
  const [alteredState, setAlteredState] = useState('');

  useEffect(() => {
    API.get(`/api/playbooks/${templateId}`).then(r => {
      setPlaybook(r.data);
      setVoices(r.data.template_config?.voices || []);
    }).catch(() => navigate('/playbooks'));
  }, [templateId, navigate]);

  // Fetch available models when entering Voice step
  useEffect(() => {
    if (step === 1 && availableModels.length === 0 && user) {
      loadModels();
    }
  }, [step, user]);

  const loadModels = async () => {
    setModelsLoading(true);
    setModelsError('');
    try {
      const res = await API.get('/api/byok/models');
      const sorted = (res.data.models || []).sort((a, b) => {
        const tierDiff = (TIER_ORDER[a.tier] || 0) - (TIER_ORDER[b.tier] || 0);
        if (tierDiff !== 0) return tierDiff;
        return a.name.localeCompare(b.name);
      });
      setAvailableModels(sorted);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('No OpenRouter key')) {
        setModelsError('No OpenRouter key configured. Go to Settings to add your key first.');
      } else {
        setModelsError('Could not load models. Check your OpenRouter key in Settings.');
      }
    }
    setModelsLoading(false);
  };

  const updateVoiceModel = (voiceIndex, modelId, modelName, tier) => {
    setVoices(prev => prev.map((v, i) =>
      i === voiceIndex ? { ...v, default_model: modelId, model_tier: tier, model_name: modelName } : v
    ));
  };

  const handleLaunch = async () => {
    if (!scenario.trim()) { setError('Enter your scenario first'); return; }
    setLaunching(true);
    setError('');
    try {
      const res = await API.post('/api/frays', {
        template_id: templateId,
        scenario: scenario,
        altered_state: alteredState || undefined,
        voice_configs: voices.map(v => ({ name: v.name, model: v.default_model, mandate: v.mandate })),
      });
      navigate(`/fray/${res.data.id}/reveal`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create Fray');
      setLaunching(false);
    }
  };

  if (!playbook) return <div className="min-h-screen bg-[#0F0F0F]"><Header /></div>;

  const steps = ['Scenario', 'Voices', 'Launch'];

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="pt-20 pb-12 px-4 md:px-6 max-w-3xl mx-auto">
        {/* Stepper */}
        <div className="flex items-center gap-2 mb-8" data-testid="gauntlet-stepper">
          {steps.map((s, i) => (
            <React.Fragment key={i}>
              <button
                onClick={() => { if (i === 0 || (i === 1 && scenario.trim()) || (i === 2 && scenario.trim())) setStep(i); }}
                className={`px-3 py-1 min-h-[44px] text-xs font-mono transition-colors ${i === step ? 'bg-[#FF4D00] text-black' : i < step ? 'text-[#FF4D00] border border-[#FF4D00]/30' : 'text-[#A0A0A0] border border-[#2A2A2A]'}`}
                data-testid={`step-${i}`}
              >{s}</button>
              {i < steps.length - 1 && <div className="w-8 h-px bg-[#2A2A2A]" />}
            </React.Fragment>
          ))}
        </div>

        <div className="text-xs font-mono text-[#A0A0A0] mb-4">{playbook.template_code} — {playbook.name}</div>

        {error && <div className="p-3 mb-4 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-sm" data-testid="gauntlet-error">{error}</div>}

        {/* Step 0: Scenario */}
        {step === 0 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <h2 className="text-2xl font-bold font-['Barlow_Condensed'] mb-2" data-testid="scenario-title">What's the decision?</h2>
            <p className="text-[#A0A0A0] text-sm mb-6">Describe the scenario you want the committee to evaluate. Be specific — the more context, the better the Fray.</p>
            <textarea
              value={scenario}
              onChange={e => setScenario(e.target.value)}
              className="w-full h-48 p-4 bg-[#1A1A1A] border border-[#2A2A2A] text-[#F5F5F5] text-sm resize-none focus:outline-none focus:border-[#FF4D00] transition-colors font-['Inter'] leading-relaxed"
              placeholder="e.g., We're building a B2B SaaS tool for AI-powered contract review. Our target market is mid-size law firms (50-200 attorneys). We have $500K in runway and a 3-person technical team..."
              data-testid="scenario-input"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs font-mono text-[#A0A0A0]">{scenario.length} / 2000</span>
              <button onClick={() => setStep(1)} disabled={!scenario.trim()} className="px-6 py-2 min-h-[44px] bg-[#FF4D00] text-black font-medium text-sm flex items-center gap-2 disabled:opacity-40" data-testid="next-step-btn">
                Next <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 1: Voices with Model Picker */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <h2 className="text-2xl font-bold font-['Barlow_Condensed'] mb-2" data-testid="voices-title">Your Committee</h2>
            <p className="text-[#A0A0A0] text-sm mb-2">{voices.length} Voices will debate your scenario. Click the model selector on each Voice to change its AI model.</p>

            {modelsError && (
              <div className="p-3 mb-4 bg-[#EF4444]/10 border border-[#EF4444]/30 text-sm flex items-start gap-2" data-testid="models-error">
                <AlertCircle className="w-4 h-4 text-[#EF4444] shrink-0 mt-0.5" />
                <div>
                  <span className="text-[#EF4444]">{modelsError}</span>
                  <button onClick={() => navigate('/settings')} className="block text-[#FF4D00] text-xs mt-1 hover:underline">Go to Settings</button>
                </div>
              </div>
            )}

            {modelsLoading && (
              <div className="p-3 mb-4 bg-[#FF4D00]/5 border border-[#FF4D00]/20 text-sm text-[#FF4D00] flex items-center gap-2">
                <div className="w-3 h-3 border border-[#FF4D00] border-t-transparent rounded-full animate-spin" />
                Loading available models from OpenRouter...
              </div>
            )}

            {!modelsError && !modelsLoading && availableModels.length > 0 && (
              <p className="text-[10px] font-mono text-[#22C55E] mb-4" data-testid="models-count">{availableModels.length} models available from your OpenRouter key</p>
            )}

            <div className="space-y-3 mb-6">
              {voices.map((v, i) => (
                <VoiceCardWithPicker
                  key={i}
                  voice={v}
                  index={i}
                  availableModels={availableModels}
                  onSelectModel={(modelId, modelName, tier) => updateVoiceModel(i, modelId, modelName, tier)}
                />
              ))}
            </div>
            <div className="flex justify-between">
              <button onClick={() => setStep(0)} className="px-4 py-2 text-sm text-[#A0A0A0] hover:text-[#F5F5F5] flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button onClick={() => setStep(2)} className="px-6 py-2 bg-[#FF4D00] text-black font-medium text-sm flex items-center gap-2" data-testid="next-step-btn">
                Review <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 2: Review & Launch */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <h2 className="text-2xl font-bold font-['Barlow_Condensed'] mb-2" data-testid="review-title">Ready to Enter the Fray</h2>
            <p className="text-[#A0A0A0] text-sm mb-6">Review your configuration and launch.</p>

            <div className="space-y-4 mb-8">
              <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                <span className="text-xs font-mono text-[#A0A0A0] block mb-1">Playbook</span>
                <span className="font-medium font-['Barlow_Condensed']">{playbook.name}</span>
              </div>
              <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                <span className="text-xs font-mono text-[#A0A0A0] block mb-1">Scenario</span>
                <p className="text-sm">{scenario}</p>
              </div>
              <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                <span className="text-xs font-mono text-[#A0A0A0] block mb-3">Committee ({voices.length} Voices)</span>
                <div className="space-y-2">
                  {voices.map((v, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#2A2A2A] last:border-0">
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 flex items-center justify-center border text-[8px] font-bold font-['Barlow_Condensed']"
                          style={{ borderColor: v.color, color: v.color }}>{v.name.charAt(0)}</div>
                        <span className="text-sm" style={{ color: v.color }}>{v.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] px-1.5 py-0.5 border font-mono"
                          style={{ color: TIER_COLORS[v.model_tier], borderColor: TIER_COLORS[v.model_tier] + '40' }}>
                          {v.model_tier?.toUpperCase()}
                        </span>
                        <span className="text-[10px] text-[#A0A0A0] font-mono max-w-[120px] md:max-w-[200px] truncate">{v.default_model}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
                <label className="text-xs text-[#A0A0A0] font-mono mb-2 block">ALTERED STATE (Optional)</label>
                <AlteredStatePicker selected={alteredState} onSelect={setAlteredState} />
              </div>
              <div className="p-4 bg-[#FF4D00]/5 border border-[#FF4D00]/20">
                <span className="text-xs font-mono text-[#FF4D00] block mb-1">Billing</span>
                <span className="text-sm">{user?.billing_mode === 'byok' ? 'Using your OpenRouter key — no credits consumed' : 'Credits will be deducted from your balance'}</span>
              </div>
            </div>

            <div className="flex justify-between">
              <button onClick={() => setStep(1)} className="px-4 py-2 text-sm text-[#A0A0A0] hover:text-[#F5F5F5] flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button
                onClick={handleLaunch} disabled={launching}
                className="px-6 md:px-8 py-3 min-h-[44px] bg-[#FF4D00] text-black font-semibold flex items-center gap-2 hover:bg-[#FF4D00]/90 transition-colors disabled:opacity-50"
                data-testid="launch-fray-btn"
              >
                {launching ? (
                  <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> Sharpening claws...</>
                ) : (
                  <><Zap className="w-4 h-4" /> Enter the Fray</>
                )}
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Voice Card with inline Model Picker                                 */
/* ------------------------------------------------------------------ */
function VoiceCardWithPicker({ voice, index, availableModels, onSelectModel }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    if (open) {
      document.addEventListener('mousedown', handler);
      setTimeout(() => searchRef.current?.focus(), 50);
    }
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = availableModels.filter(m => {
    const matchSearch = !search || m.id.toLowerCase().includes(search.toLowerCase()) || m.name.toLowerCase().includes(search.toLowerCase());
    const matchTier = tierFilter === 'all' || m.tier === tierFilter;
    return matchSearch && matchTier;
  });

  const currentModelName = voice.model_name || voice.default_model?.split('/').pop() || voice.default_model;

  return (
    <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]" data-testid={`gauntlet-voice-${index}`}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 flex items-center justify-center border-2 text-xs font-bold font-['Barlow_Condensed'] shrink-0"
          style={{ borderColor: voice.color, color: voice.color }}>{voice.name.charAt(0)}</div>
        <div className="flex-1 min-w-0">
          <span className="font-medium font-['Barlow_Condensed'] text-sm">{voice.name}</span>
          <p className="text-xs text-[#A0A0A0] mt-0.5 mb-3">{voice.mandate}</p>

          {/* Model Selector Button */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setOpen(!open)}
              className={`w-full flex items-center justify-between px-3 py-2 border text-left transition-colors ${
                open ? 'border-[#FF4D00] bg-[#FF4D00]/5' : 'border-[#2A2A2A] hover:border-[#FF4D00]/40'
              }`}
              data-testid={`model-picker-${index}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[9px] px-1.5 py-0.5 border font-mono shrink-0"
                  style={{ color: TIER_COLORS[voice.model_tier], borderColor: TIER_COLORS[voice.model_tier] + '40' }}>
                  {voice.model_tier?.toUpperCase()}
                </span>
                <span className="text-xs font-mono text-[#F5F5F5] truncate">{voice.default_model}</span>
              </div>
              <ChevronDown className={`w-3.5 h-3.5 text-[#A0A0A0] shrink-0 ml-2 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown */}
            <AnimatePresence>
              {open && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.12 }}
                  className="absolute z-50 left-0 right-0 mt-1 bg-[#1A1A1A] border border-[#2A2A2A] shadow-xl shadow-black/50 max-h-[320px] flex flex-col"
                  data-testid={`model-dropdown-${index}`}
                >
                  {/* Search */}
                  <div className="p-2 border-b border-[#2A2A2A]">
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 text-[#A0A0A0] absolute left-2.5 top-1/2 -translate-y-1/2" />
                      <input
                        ref={searchRef}
                        type="text" value={search} onChange={e => setSearch(e.target.value)}
                        className="w-full pl-8 pr-8 py-1.5 bg-[#0F0F0F] border border-[#2A2A2A] text-xs text-[#F5F5F5] focus:outline-none focus:border-[#FF4D00]"
                        placeholder="Search models..."
                        data-testid={`model-search-${index}`}
                      />
                      {search && (
                        <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A0A0A0]">
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    {/* Tier filter */}
                    <div className="flex gap-1 mt-2">
                      {['all', 'economy', 'standard', 'premium'].map(t => (
                        <button
                          key={t}
                          onClick={() => setTierFilter(t)}
                          className={`px-2 py-0.5 text-[9px] font-mono border transition-colors ${
                            tierFilter === t
                              ? t === 'all' ? 'bg-[#FF4D00] text-black border-[#FF4D00]'
                                : `border-current` + (t === 'economy' ? ' text-[#22C55E] bg-[#22C55E]/10' : t === 'standard' ? ' text-[#F59E0B] bg-[#F59E0B]/10' : ' text-[#EF4444] bg-[#EF4444]/10')
                              : 'border-[#2A2A2A] text-[#A0A0A0]'
                          }`}
                          data-testid={`tier-filter-${t}-${index}`}
                        >{t === 'all' ? 'ALL' : t.toUpperCase()}</button>
                      ))}
                    </div>
                  </div>

                  {/* Model list */}
                  <div className="overflow-y-auto flex-1">
                    {availableModels.length === 0 ? (
                      <div className="p-4 text-center text-xs text-[#A0A0A0]">
                        No models available. Add your OpenRouter key in Settings.
                      </div>
                    ) : filtered.length === 0 ? (
                      <div className="p-4 text-center text-xs text-[#A0A0A0]">
                        No models match "{search}"
                      </div>
                    ) : (
                      filtered.slice(0, 50).map((m, mi) => {
                        const isSelected = voice.default_model === m.id;
                        return (
                          <button
                            key={m.id}
                            onClick={() => {
                              onSelectModel(m.id, m.name, m.tier);
                              setOpen(false);
                              setSearch('');
                            }}
                            className={`w-full flex items-center justify-between px-3 py-2 text-left transition-colors border-b border-[#0F0F0F] ${
                              isSelected ? 'bg-[#FF4D00]/10' : 'hover:bg-[#2A2A2A]/50'
                            }`}
                            data-testid={`model-option-${index}-${mi}`}
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className="text-[8px] w-12 text-center px-1 py-0.5 border font-mono shrink-0"
                                style={{ color: TIER_COLORS[m.tier], borderColor: TIER_COLORS[m.tier] + '40' }}>
                                {m.tier?.toUpperCase()?.slice(0, 4)}
                              </span>
                              <div className="min-w-0 flex-1">
                                <span className="text-xs text-[#F5F5F5] block truncate">{m.name || m.id}</span>
                                <span className="text-[9px] text-[#A0A0A0] font-mono block truncate">{m.id}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 ml-2">
                              {m.context_length && (
                                <span className="text-[8px] font-mono text-[#A0A0A0]">{Math.round(m.context_length / 1000)}k ctx</span>
                              )}
                              {isSelected && <Check className="w-3.5 h-3.5 text-[#FF4D00]" />}
                            </div>
                          </button>
                        );
                      })
                    )}
                    {filtered.length > 50 && (
                      <div className="p-2 text-center text-[9px] text-[#A0A0A0] font-mono">
                        Showing 50 of {filtered.length} — narrow your search
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
